/**
 * Every campaign the automation could work (active and paused), and what it
 * thinks of each one right now.
 *
 * One line per campaign: its name as a link to its page, whether it is an
 * offer or a defect alert, its own automation switch, today's calls against its
 * cap, the queue (waiting, and how many of those may be tried now), and the
 * verdict: "next call comes from here", "ready", or the policy's reason for
 * skipping it (`campaignSkip`). The facts behind a verdict follow it: the next
 * retry, a temporary block, the campaign's dates.
 *
 * "Call the next one now" sits on every active campaign for managers. It is
 * disabled while AI calling is off, which the server would refuse anyway.
 */
import { Link } from "react-router-dom";
import { Megaphone } from "lucide-react";
import { useI18n } from "../../../../i18n/I18nProvider";
import { CAMPAIGN_STATUSES, type CampaignStatus } from "../../../../lib/aiCampaigns";
import type { AutomationStatus } from "../../../../lib/aiAutomation";
import { StatusPill, fmtNum } from "../../../ui";
import { Panel } from "../../marketing/shared";
import { campaignStatusTone } from "../campaigns/campaignTone";
import { CallNextButton } from "./CallNextButton";
import { campaignLine, fmtWhen, offersCallNext, viewerTimeZone } from "./automationView";

const STATUSES: ReadonlySet<string> = new Set<string>(CAMPAIGN_STATUSES);

export function AutomationCampaignsCard({
  status,
  canManage,
  onChanged,
}: {
  status: AutomationStatus;
  canManage: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const when = (iso: string | null) => fmtWhen(iso, status.now, locale, status.settings.timezone, viewerTimeZone());
  const now = Date.parse(status.now);

  return (
    <Panel
      title={t("aiCalls.automation.campaigns.title")}
      description={t("aiCalls.automation.campaigns.intro")}
      icon={<Megaphone className="size-[18px]" strokeWidth={1.7} />}
    >
      {status.campaigns.length === 0 ? (
        <p className="crm-sub mt-0 text-[13px]">{t("aiCalls.automation.campaigns.empty")}</p>
      ) : (
        <ul className="-mx-[26px] divide-y divide-border">
          {status.campaigns.map((campaign) => {
            const line = campaignLine(campaign, status.decision);
            const facts: string[] = [
              t("aiCalls.automation.campaigns.today", { used: fmtNum(campaign.usedToday), cap: fmtNum(campaign.dailyCap) }),
              t("aiCalls.automation.campaigns.queue", { waiting: fmtNum(campaign.waiting), due: fmtNum(campaign.due) }),
            ];
            if (campaign.nextDueAt) facts.push(t("aiCalls.automation.campaigns.nextDue", { when: when(campaign.nextDueAt) }));
            if (campaign.blockedUntil) facts.push(t("aiCalls.automation.campaigns.blockedUntil", { when: when(campaign.blockedUntil) }));
            if (campaign.startsAt && Date.parse(campaign.startsAt) > now) {
              facts.push(t("aiCalls.automation.campaigns.startsAt", { when: when(campaign.startsAt) }));
            }
            if (campaign.endsAt) facts.push(t("aiCalls.automation.campaigns.endsAt", { when: when(campaign.endsAt) }));
            return (
              <li key={campaign.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-[26px] py-3">
                <div className="min-w-0 flex-1 basis-60">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/ai-calls/campaigns/${campaign.id}`} className="font-medium hover:underline">
                      {campaign.name || t("aiCalls.campaigns.untitled")}
                    </Link>
                    <StatusPill tone={campaign.angle === "alert" ? "info" : "mut"}>{t(`aiCalls.campaigns.angle.${campaign.angle}`)}</StatusPill>
                    {campaign.urgentDue ? <StatusPill tone="lt">{t("aiCalls.automation.campaigns.urgent")}</StatusPill> : null}
                    {campaign.status !== "active" && STATUSES.has(campaign.status) ? (
                      <StatusPill tone={campaignStatusTone(campaign.status as CampaignStatus)}>
                        {t(`aiCalls.campaigns.status.${campaign.status}`)}
                      </StatusPill>
                    ) : null}
                    <StatusPill tone={campaign.autoEnabled ? "ok" : "mut"}>
                      {t(campaign.autoEnabled ? "aiCalls.automation.campaigns.autoOn" : "aiCalls.automation.campaigns.autoOff")}
                    </StatusPill>
                  </div>
                  <p className="crm-sub mt-1 text-xs">{facts.join(" · ")}</p>
                </div>
                <StatusPill tone={line.tone}>{t(line.key)}</StatusPill>
                {offersCallNext(campaign, canManage) ? (
                  <CallNextButton campaignId={campaign.id} disabled={!status.settings.enabled} onCalled={onChanged} />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
